import * as React from "react";
import * as _ from "lodash";
import { Static } from "@bizform/type/decorator/Static.decorator";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { WindowKey } from "@bizform/type/model/model/WindowKey.model";
import { PluginType } from "@bizform/type/model/model/Plugin.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    AlignContent,
    AlignItems,
    FlexDirection,
    FlexWrap,
    JustifyContent
} from "@bizform/type/model/data_set/LookUp.model";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";

type Props = {
};

enum FlexBoxProperty {
    FLEX_DIRECTION = "flexDirection",
    JUSTIFY_CONTENT = "justifyContent",
    ALIGN_ITEMS = "alignItems",
    ALIGN_CONTENT = "alignContent",
    FLEX_WRAP = "flexWrap",
    FLEX_WRAP_SM = "flexWrapSm",
    FLEX_WRAP_MD = "flexWrapMd",
    FLEX_WRAP_LG = "flexWrapLg",
    FLEX_WRAP_XL = "flexWrapXl",
}

type WrappedCompState = {
    [FlexBoxProperty.FLEX_DIRECTION]?: FlexDirection;
    [FlexBoxProperty.JUSTIFY_CONTENT]?: JustifyContent;
    [FlexBoxProperty.ALIGN_ITEMS]?: AlignItems;
    [FlexBoxProperty.ALIGN_CONTENT]?: AlignContent;
    [FlexBoxProperty.FLEX_WRAP]?: FlexWrap;
    [FlexBoxProperty.FLEX_WRAP_SM]?: FlexWrap;
    [FlexBoxProperty.FLEX_WRAP_MD]?: FlexWrap;
    [FlexBoxProperty.FLEX_WRAP_LG]?: FlexWrap;
    [FlexBoxProperty.FLEX_WRAP_XL]?: FlexWrap;
    containerModel?: any;
    className?: string;
    style?: any;
};

const ___responsiveFlexWrapProperty: object = {
    [FlexBoxProperty.FLEX_WRAP_SM]: true,
    [FlexBoxProperty.FLEX_WRAP_MD]: true,
    [FlexBoxProperty.FLEX_WRAP_LG]: true,
    [FlexBoxProperty.FLEX_WRAP_XL]: true
};

enum ResponsiveStyleProperty {
    STYLE_SM = "styleSm",
    STYLE_MD = "styleMd",
    STYLE_LG = "styleLg",
    STYLE_XL = "styleXl"
}

enum FlexLayoutContainerType {
    MAIN = "main",
    SUB = "sub"
}

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

type InternalState = {
    responsiveStyle: string;
    tmpResponsiveStyleArr: Array<string>;
    tmpMarkup: object;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park, Jongkwang Park
 * @class FlexLayout
 * @description A layout component that defines a CSS flexible box model optimized for user interface design.
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfFlexLayout extends AbstractComponentElement<Props, State> {
    static readonly pluginType: string = PluginType.COMPONENTELEMENT;
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_rbKoiltw9SiHUyZLPwqT2iB7sM6B4vm",
        category: ComponentCategory.PANEL,
        label: I18n.layout.flexLayout.label,
        description: I18n.layout.flexLayout.description,
        icon: "",
        property: null
    };

    destroyed: boolean;
    accessibility: boolean;
    elementId: string;
    applicationId: string;
    formId: string;
    dataType: DataType;
    displayMode: ComponentElementDisplayMode;
    defaultDateFormat: string;
    parentDom: HTMLElement;

    /* for React */
    _state: InternalState;
    state: State;
    private _flexLayoutDiv: HTMLElement;

    constructor(props?: any) {
        super(props);

        this._state = {
            responsiveStyle: null,
            tmpResponsiveStyleArr: [],
            tmpMarkup: {}
        };
        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {}
        };

        this._setRef = this._setRef.bind(this);
        this._childBox = this._childBox.bind(this);
    }

    emitHasContainerKey(): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfFlexLayout)
    static init(): ComponentElement<null> {
        return;
    }

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this._setStyle(newState.wrappedCompState);
            this._setResponsiveStyle(newState.wrappedCompState);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    private _setStyle(newWrappedState: any): void {
        newWrappedState.style = newWrappedState.style ? newWrappedState.style : {};
        Object.keys(FlexBoxProperty).forEach(function (currentFlexPropertyKey, index, callerArray) {
            if (!___responsiveFlexWrapProperty.hasOwnProperty(currentFlexPropertyKey)) {
                let currentFlexProperty = newWrappedState[FlexBoxProperty[currentFlexPropertyKey]];
                newWrappedState.style[FlexBoxProperty[currentFlexPropertyKey]] = currentFlexProperty;
            }
        });
    }

    private _markResponsiveStyle(stylePropertyName: string, containerType: string, containerId: string, wrappedState: any, tmpMarkup: object) {
        const stylePropertyObject = wrappedState[stylePropertyName] && typeof wrappedState[stylePropertyName] === "object" ? wrappedState[stylePropertyName] : {};
        const stylePropertyObjectKeys = Object.keys(stylePropertyObject);
        const hasResponsiveStyle = stylePropertyObjectKeys.length > 0;
        if (hasResponsiveStyle) {
            const responsiveStyles = [];
            for (let idx = 0; idx < stylePropertyObjectKeys.length; ++idx) {
                const currentStyleKey = stylePropertyObjectKeys[idx];
                this.___forEachStylePropertyObjectKeys(stylePropertyObject, responsiveStyles, currentStyleKey);
            }

            if (!tmpMarkup[stylePropertyName]) {
                tmpMarkup[stylePropertyName] = {};
            }
            if (responsiveStyles && responsiveStyles.length > 0) {
                if (!tmpMarkup[stylePropertyName][containerType]) {
                    tmpMarkup[stylePropertyName][containerType] = {};
                }
                if (!tmpMarkup[stylePropertyName][containerType][containerId]) {
                    tmpMarkup[stylePropertyName][containerType][containerId] = {};
                }
                tmpMarkup[stylePropertyName][containerType][containerId] = responsiveStyles;
            }
        }
    }

    private ___forEachStylePropertyObjectKeys(stylePropertyObject: object, responsiveStyles: Array<string>, currentStyleKey: string) {
        responsiveStyles.push(`${_.kebabCase(currentStyleKey)}: ${this._appendImportant(stylePropertyObject[currentStyleKey])};`);
    }

    /**
     * Media 쿼리의 min-width로 사용할 width 얻기
     * @param stylePropertyName
     * @private
     */
    private _getAppropriateMinWidthForScreenSize(stylePropertyName: ResponsiveStyleProperty): number {
        let width = 0;
        if (stylePropertyName === ResponsiveStyleProperty.STYLE_SM) {
            width = 576;
        } else if (stylePropertyName === ResponsiveStyleProperty.STYLE_MD) {
            width = 768;
        } else if (stylePropertyName === ResponsiveStyleProperty.STYLE_LG) {
            width = 992;
        } else if (stylePropertyName === ResponsiveStyleProperty.STYLE_XL) {
            width = 1200;
        }
        return width;
    }

    /**
     * Responsive Style 설정을 위한 medial queries 문을 생성한다.
     * @param stylePropertyName
     * @param responsiveStyles
     * @param tmpMarkup
     * @param mainContainerId
     * @private
     */
    private _makeResponsiveStyle(stylePropertyName: ResponsiveStyleProperty, mainContainerId: string, tmpResponsiveStyleArr: Array<string>) {
        const tmpMarkup = this._state.tmpMarkup;
        const minWidth = this._getAppropriateMinWidthForScreenSize(stylePropertyName);

        tmpResponsiveStyleArr.push(`@media screen and (min-width: ${minWidth}px) {`);
        if (_.has(tmpMarkup, `${stylePropertyName}.main.${mainContainerId}`)) {
            tmpResponsiveStyleArr.push(`div[bf-custom-id="${mainContainerId}"] {`);
            const responsiveStyleArr = _.get(tmpMarkup, `${stylePropertyName}.main.${mainContainerId}`, []);
            for (let idx = 0; idx < responsiveStyleArr.length; ++idx) {
                tmpResponsiveStyleArr.push(responsiveStyleArr[idx]);
            }
            tmpResponsiveStyleArr.push("}");
        }

        const flexItemResponsiveStyleObj = _.get(tmpMarkup, `${stylePropertyName}.sub`, {});
        const flexItemResponsiveStyleObjKeys = Object.keys(flexItemResponsiveStyleObj);
        for (let jdx = 0; jdx < flexItemResponsiveStyleObjKeys.length; ++jdx) {
            const flexItemId = flexItemResponsiveStyleObjKeys[jdx];
            this.___forEachFlexItemResponsiveStyleObj(tmpResponsiveStyleArr, stylePropertyName, flexItemId, mainContainerId);
        }
        tmpResponsiveStyleArr.push("}");
    }

    private ___forEachFlexItemResponsiveStyleObj(responsiveStyles: Array<string>, stylePropertyName: string, flexItemId: string, mainContainerId: string) {
        const tmpMarkup = this._state.tmpMarkup;
        if (_.has(tmpMarkup, `${stylePropertyName}.sub.${flexItemId}`)) {
            responsiveStyles.push(`div[bf-custom-id="${mainContainerId}"] > div[bf-container="${flexItemId}"] {`);
            const responsiveStyleArr = _.get(tmpMarkup, `${stylePropertyName}.sub.${flexItemId}`, []);
            for (let jdx = 0; jdx < responsiveStyleArr.length; ++jdx) {
                responsiveStyles.push(responsiveStyleArr[jdx]);
            }
            responsiveStyles.push("}");
        }
    }

    /**
     * Responsive Style 설정을 위한 attributes(styleSm, styleMd, styleLg, styleXl)에 기반하여
     * media queries를 생성하여 this._state.responsiveStyle에 할당한다.
     * @param wrappedState
     * @private
     */
    private _setResponsiveStyle(wrappedState: any): void {
        if (!this._state.tmpMarkup) {
            this._state.tmpMarkup = {};
        }
        const tmpMarkup = this._state.tmpMarkup;
        const mainContainerId = `${this._getPrefixContainerKey()}-${FlexLayoutContainerType.MAIN}`;
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_SM, FlexLayoutContainerType.MAIN, mainContainerId, wrappedState, tmpMarkup);
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_MD, FlexLayoutContainerType.MAIN, mainContainerId, wrappedState, tmpMarkup);
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_LG, FlexLayoutContainerType.MAIN, mainContainerId, wrappedState, tmpMarkup);
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_XL, FlexLayoutContainerType.MAIN, mainContainerId, wrappedState, tmpMarkup);

        if (Array.isArray(wrappedState.containerModel) && wrappedState.containerModel.length > 0) {
            wrappedState.containerModel.map(this.___mapContainerModel, this);
        }

        const hasResponsiveStyleSm = tmpMarkup[ResponsiveStyleProperty.STYLE_SM] && Object.keys(tmpMarkup[ResponsiveStyleProperty.STYLE_SM]).length > 0;
        const hasResponsiveStyleMd = tmpMarkup[ResponsiveStyleProperty.STYLE_MD] && Object.keys(tmpMarkup[ResponsiveStyleProperty.STYLE_MD]).length > 0;
        const hasResponsiveStyleLg = tmpMarkup[ResponsiveStyleProperty.STYLE_LG] && Object.keys(tmpMarkup[ResponsiveStyleProperty.STYLE_LG]).length > 0;
        const hasResponsiveStyleXl = tmpMarkup[ResponsiveStyleProperty.STYLE_XL] && Object.keys(tmpMarkup[ResponsiveStyleProperty.STYLE_XL]).length > 0;

        if (hasResponsiveStyleSm || hasResponsiveStyleMd || hasResponsiveStyleLg || hasResponsiveStyleXl) {
            if (this._state.tmpResponsiveStyleArr.length > 0) {
                this._state.tmpResponsiveStyleArr.splice(0, this._state.tmpResponsiveStyleArr.length);
            }
            const tmpResponsiveStyleArr = this._state.tmpResponsiveStyleArr;

            if (hasResponsiveStyleSm) {
                this._makeResponsiveStyle(ResponsiveStyleProperty.STYLE_SM, mainContainerId, tmpResponsiveStyleArr);
            }
            if (hasResponsiveStyleMd) {
                this._makeResponsiveStyle(ResponsiveStyleProperty.STYLE_MD, mainContainerId, tmpResponsiveStyleArr);
            }
            if (hasResponsiveStyleLg) {
                this._makeResponsiveStyle(ResponsiveStyleProperty.STYLE_LG, mainContainerId, tmpResponsiveStyleArr);
            }
            if (hasResponsiveStyleXl) {
                this._makeResponsiveStyle(ResponsiveStyleProperty.STYLE_XL, mainContainerId, tmpResponsiveStyleArr);
            }

            this._state.responsiveStyle = this._state.tmpResponsiveStyleArr.join("\n");
            // empty this._state.tmpResponsiveStyleArr(instead of this._state.tmpResponsiveStyleArr = [] or this._state.tmpResponsiveStyleArr.length = 0)
            this._state.tmpResponsiveStyleArr.splice(0, this._state.tmpResponsiveStyleArr.length);
        } else {
            this._state.responsiveStyle = null;
        }
        this._state.tmpMarkup = {};
    }

    private ___mapContainerModel(item: any, index: number) {
        const tmpMarkup = this._state.tmpMarkup;
        const subContainerId = `${this._getPrefixSubContainerKey()}-${item.key}`;
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_SM, FlexLayoutContainerType.SUB, subContainerId, item, tmpMarkup);
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_MD, FlexLayoutContainerType.SUB, subContainerId, item, tmpMarkup);
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_LG, FlexLayoutContainerType.SUB, subContainerId, item, tmpMarkup);
        this._markResponsiveStyle(ResponsiveStyleProperty.STYLE_XL, FlexLayoutContainerType.SUB, subContainerId, item, tmpMarkup);
    }

    /**
     * style에 "!important"를 붙여준다.
     * @param value
     * @private
     */
    private _appendImportant(value: string): string {
        if (value && !_.endsWith(_.toLower(value), "\!important")) {
            return value + " !important";
        }
        return value;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    readyComponent(): void {
    }

    componentWillUnmount(): void {
        this._flexLayoutDiv = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    onEdit(): void {
        this._displayMode();
    }

    onReadOnly(): void {
        this._displayMode();
    }

    onDisabled(): void {
        this._displayMode();
    }

    onPrint(): void {
        this._displayMode();
    }

    private _displayMode() {
        this.setState({
            displayMode: this.displayMode
        });
    }

    render(): React.ReactNode {
        if (!this.displayMode) {
            return null;
        } else {
            const newState = this.state.wrappedCompState;
            const bfContainer = Array.isArray(newState.containerModel) && newState.containerModel.length > 0 ? null : {[DomAttribute.CONTAINER.toString()]: this._getPrefixContainerKey()};
            return <React.Fragment>
                { this._createResponsiveStyle() }
                <div bf-custom-id={`${this._getPrefixContainerKey()}-${FlexLayoutContainerType.MAIN}`} ref={this._setRef} className={"d-flex " + (newState.className ? newState.className : "")} style={{...newState.style}} {...bfContainer}>
                    {Array.isArray(newState.containerModel) && newState.containerModel.length > 0 ? newState.containerModel.map(this._childBox) : null}
                </div>
            </React.Fragment>;
        }
    }

    /**
     * Responsive 웹 디자인에 필요한 속성들(sm, md, lg, xl)을 설정하였다면 media queries 스타일을 만든다.
     * sm, md, lg, xl으로 구분되지 않는 Style(기본값)은 xs(extra small)을 의미한다.
     * Styled component와 같이 CSS in JS 기법으로 FlexLayout 컴포넌트에 필요 media queries를 같이 렌더링해준다.
     * @private
     */
    private _createResponsiveStyle(): React.ReactNode {
        if (this._state.responsiveStyle) {
            return <style>
                {this._state.responsiveStyle}
            </style>;
        } else {
            return null;
        }
    }

    private _getPrefixContainerKey(): string {
        return `flex-layout${this.elementId}`;
    }

    private _getPrefixSubContainerKey(): string {
        return "flex-layout-item";
    }

    /**
     * Container Model 개체에 담겨 있는 FlexLayout sub item 박스의 flex 속성들을 style 필드에 할당한다.
     * @param containerModelItem
     * @private
     */
    private _setChildBoxStyle(containerModelItem: any) {
        if (!containerModelItem.style) {
            containerModelItem.style = {};
        }
        containerModelItem.style.flexGrow = _.defaultTo(containerModelItem.flexGrow, 1);
        containerModelItem.style.flexShrink = _.defaultTo(containerModelItem.flexShrink, 1);
        containerModelItem.style.flexBasis = _.defaultTo(containerModelItem.flexBasis, "auto");
        if (containerModelItem.alignSelf) {
            containerModelItem.style.alignSelf = containerModelItem.alignSelf;
        }
        containerModelItem.style.order = containerModelItem.order;
    }

    /**
     * FlexLayout이 가지는 sub item 박스들의 렌더링 함수
     * @param p
     * @private
     */
    private _childBox(p: any): React.ReactNode {
        const containerString = {[DomAttribute.CONTAINER.toString()]: `${this._getPrefixSubContainerKey()}-${p.key}`};
        const designModeStyle: any = {width: "100%", textAlign: "center", fontSize: "xx-small", minWidth: "50px", color: "grey", display: !window[WindowKey.DESIGNMODE] ? "none" : "block"};
        const containerModelItem = p ? ObjectHelper.jsonCloneDeep(p) : {};
        this._setChildBoxStyle(containerModelItem);

        if (window[WindowKey.DESIGNMODE]) {
            const opacity = "0.3";
            if (containerModelItem.className.indexOf("d-none") > -1) {
                containerModelItem.className = _.replace(containerModelItem.className, "d-none", "");
                containerModelItem.style.opacity = opacity;
            } else {
                if (containerModelItem.style.display && containerModelItem.style.display === "none") {
                    delete containerModelItem.style.display;
                    containerModelItem.style.opacity = opacity;
                }
            }
        }
        return <div {...containerString} className={containerModelItem.className} style={{...containerModelItem.style}}>
            <div style={{...designModeStyle}}>[{containerModelItem.key}]</div>
        </div>;
    }

    private _setRef(el): void {
        if (el) {
            this._flexLayoutDiv = el;
            this.readyComponent();
        } else {
            if (this._flexLayoutDiv) {
                this._flexLayoutDiv = null;
            }
        }
    }
}